import React, { FunctionComponent } from 'react';
import { Routes, Route } from 'react-router-dom';
import * as SentryReact from '@sentry/react';
import { CheckupComplete } from './pages/CheckupComplete';
import { AndroidHelp } from './pages/AndroidHelp';
import { IOSHelp } from './pages/IOSHelp';
import { CheckupWorkflow } from './workflow/CheckupWorkflow';
import { PageDefinitionSchema, ValidatedPageDefinition, ConvertCheckupRequestV2ResponseToInfo, CheckupRequestV2Response } from '@companion-professional/dctypes';
import { z } from 'zod';
import { ErrorWithHeader } from '@companion-professional/components';
import { CaptureOrUpload } from './pages/CaptureOrUpload';

const SentryRoutes = SentryReact.withSentryReactRouterV6Routing(Routes);

type CheckupWorkflowRouterProps = {
  checkupRequestv2Response: CheckupRequestV2Response;
};

// getValidatedUiDefn validates the checkup request UI page definition
const getValidatedUiDefn = (uiPageDefn: Array<unknown>) => {
  // Validate that the ui array contains valid PageDefinitionSchema objects
  const uiArraySchema = z.array(PageDefinitionSchema);

  // Parse and validate the entire ui array
  const validatedUiPageDefn = uiArraySchema.safeParse(uiPageDefn);
  return validatedUiPageDefn;
}

// CheckupRoutes contains all the routes for the checkup flow.
export const CheckupWorkflowRouter: FunctionComponent<CheckupWorkflowRouterProps> = ({ checkupRequestv2Response }) => {

  if (!checkupRequestv2Response.checkup_type || checkupRequestv2Response.checkup_type.ui.pages.length === 0) {
    console.error('UI validation failed: checkup_type.ui.pages is empty array');
    return (
      <ErrorWithHeader 
        title="Error" 
        message="Pages cannot be empty for the checkup request. Please contact us for assisstance!" 
      />
    )
  }
  
  // Currently, CheckupWorkflow only cares about the ui pages, and nothing else as
  // of yet, so that's only I am validating it here with zod. However, the good thing
  // is that if we there extra attributes needed in checkup_type etc. we can easily
  // extend the schema, and CheckupWorkflow in future.
  const isValidResult = getValidatedUiDefn(checkupRequestv2Response.checkup_type.ui.pages);
  if (!isValidResult.success) {
    console.error('UI validation failed:', isValidResult.error.issues);
    // Do not want the app to crash, but maybe show a pop up to the user
    // to contant support? TODO
    return (
      <ErrorWithHeader 
        title="Error" 
        message="UI validation failed. Please contact us for assisstance!" 
      />
    );
  }

  const uiPageDefn: ValidatedPageDefinition[] = isValidResult.data;
  const checkupRequest = ConvertCheckupRequestV2ResponseToInfo(checkupRequestv2Response);

  return (
    <SentryRoutes>
      {/* Dynamically generate routes for each page in the uiDefn array */}
      {uiPageDefn.map((_, index) => (
        <Route
          key={index}
          path={`/${index}`}
          element={<CheckupWorkflow uiPageDefn={uiPageDefn} checkupRequest={checkupRequest} page={index} />}
        />
      ))}
      {/* Map root path to page 0 as the URL we send to users and link to from vportal is just /checkup_id. */}
      <Route
        path="/"
        element={<CheckupWorkflow uiPageDefn={uiPageDefn} checkupRequest={checkupRequest} page={0} />}
      />

      {/* STATIC ROUTES: These pages will exist and come at the end of the workflow*/}
      <Route path="/video" element={<CaptureOrUpload checkupRequest={checkupRequest} />} />
      <Route path="/complete" element={<CheckupComplete checkupRequest={checkupRequest} />} />
      <Route path="/android-help" element={<AndroidHelp />} />
      <Route path="/ios-help" element={<IOSHelp />} />
      
      <Route path="*" element={
        <ErrorWithHeader 
          title="Page Not Found" 
          message="The page you're looking for doesn't exist. Please contact us for assistance!" 
        />
      } />
    </SentryRoutes>
  );
};
